import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { listArchivedTrips, deleteTrip, type Trip } from '@/services/tripService'

function ArchivedTripCard({ trip }: { trip: Trip }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [confirming, setConfirming] = useState(false)

  const deleteMutation = useMutation({
    mutationFn: () => deleteTrip(trip.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['archivedTrips'] })
    },
  })

  return (
    <div className="overflow-hidden rounded-lg border border-line bg-panel">
      <div className="h-24" style={{ background: trip.cover_url ?? undefined, opacity: 0.6 }} />
      <div className="p-5">
        <h3 className="font-display text-xl text-ivory">{trip.title}</h3>
        <div className="mb-3 mt-1 text-sm text-mist">{trip.destination}</div>
        <div className="mb-3 font-mono text-xs text-brass-dim">
          {trip.start_date} – {trip.end_date}
        </div>

        {deleteMutation.isError && (
          <p role="alert" className="mb-2 text-sm text-brass-warm">
            {t('archivedTrips.deleteError')}
          </p>
        )}

        {confirming ? (
          <div className="rounded-r border-l-[3px] border-line border-l-brass-warm bg-panel-2 px-4 py-3">
            <p className="mb-2.5 text-sm text-ivory">{t('archivedTrips.confirmDelete')}</p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setConfirming(false)}
                className="rounded border border-line-strong px-3 py-1.5 font-sans text-xs text-ivory"
              >
                {t('archivedTrips.cancel')}
              </button>
              <button
                type="button"
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="rounded border border-brass-warm px-3 py-1.5 font-sans text-xs font-medium text-brass-warm disabled:opacity-50"
              >
                {t('archivedTrips.confirmDeleteButton')}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            className="font-sans text-xs text-mist hover:text-brass-warm"
          >
            {t('archivedTrips.delete')}
          </button>
        )}
      </div>
    </div>
  )
}

export function ArchivedTripsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: trips, isLoading } = useQuery({
    queryKey: ['archivedTrips'],
    queryFn: listArchivedTrips,
  })

  return (
    <div className="min-h-screen bg-night px-12 py-10">
      <button
        type="button"
        onClick={() => navigate('/trips')}
        className="mb-6 font-sans text-sm text-mist"
      >
        {t('newTrip.backToTrips')}
      </button>

      <h1 className="mb-8 font-display text-4xl text-ivory">{t('archivedTrips.title')}</h1>

      {isLoading ? (
        <p className="text-mist">{t('archivedTrips.loading')}</p>
      ) : trips && trips.length > 0 ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-5">
          {trips.map((trip) => (
            <ArchivedTripCard key={trip.id} trip={trip} />
          ))}
        </div>
      ) : (
        <p className="text-mist">{t('archivedTrips.empty')}</p>
      )}
    </div>
  )
}
